// Reads `**` / `*` inside Japanese (CJK) sentences as emphasis.
//
// CommonMark's delimiter rule (left/right-flanking) says that when the outside
// of a delimiter is a letter, the inside must be a letter too. English separates
// words with spaces so this rarely matters, but Japanese has no spaces, so any
// emphasis whose inside starts or ends with punctuation stays literal. With K
// for a kana or kanji and the full-width corner brackets as the quotes:
//
//   K**<open>bold<close>**K   <- `<close>**K`: punctuation inside, letter outside -> does not close
//   K**<open>bold<close>**    <- `K**<open>`: letter outside, punctuation inside -> does not open
//
// Like markdown-it-cjk-friendly, a CJK character on the *outside* is treated as
// punctuation. The rule for Latin letters (`a**"foo"**b` is not emphasis) is
// unchanged. `_` keeps the "no emphasis inside a word" rule (`snake_case_name`).
//
// marked's built-in `emStrong` does the judging; only the characters it sees change:
// - opening: a CJK previous character (`prevChar`) is passed as punctuation;
// - closing: candidates are found by regex in `maskedSrc` (after
//   `emStrongMask`), so in "punctuation + run of `*` + CJK" the CJK character is
//   replaced by punctuation. Only after punctuation, so that a `*` between two
//   letters (`**out*in*out**`) keeps its meaning. The replacement keeps the
//   UTF-16 length (marked slices `maskedSrc` and the source at the same indexes).
import { type MarkedExtension, Tokenizer } from "marked";

const CJK =
  "\\p{Script=Han}\\p{Script=Hiragana}\\p{Script=Katakana}\\p{Script=Hangul}\\u3005\\u3006\\u30FC\\uFF10-\\uFF19\\uFF21-\\uFF3A\\uFF41-\\uFF5A\\uFF66-\\uFF9F";
const CJK_CHAR = new RegExp(`^[${CJK}]$`, "u");
const CLOSE_BEFORE_CJK = new RegExp(`([\\p{P}\\p{S}]\\*+)([${CJK}])`, "gu");

/** The character put in place of one treated as punctuation (`\p{P}`). */
// i18n-ignore: a stand-in character for the tokenizer, never shown
const PUNCT = "、";

const builtinEmStrong = Tokenizer.prototype.emStrong;

export const cjkEmphasis: MarkedExtension = {
  tokenizer: {
    emStrong(src, maskedSrc, prevChar = "") {
      if (src[0] !== "*" || !CJK_CHAR.test(prevChar)) return false;
      return builtinEmStrong.call(this, src, maskedSrc, PUNCT) ?? false;
    },
  },
  hooks: {
    emStrongMask(src) {
      return src.replace(
        CLOSE_BEFORE_CJK,
        (_, run: string, ch: string) => run + PUNCT.repeat(ch.length),
      );
    },
  },
};
